import React, { useEffect, useState, useMemo } from 'react';
import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  TableSortLabel,
} from '@mui/material';
import DateRangeFilter from '../common/DateRangeFilter';
import { Report } from '../../models/report';

const getMonday = () => {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(now.setDate(diff));
  return monday.toISOString().split('T')[0];
};

const getSunday = () => {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? 0 : 7);
  const sunday = new Date(now.setDate(diff));
  return sunday.toISOString().split('T')[0];
};

const OrderStaffActivityReport = (props) => {
  const { setPageTitle } = props;
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [startDate, setStartDate] = useState(getMonday());
  const [endDate, setEndDate] = useState(getSunday());
  const [sortField, setSortField] = useState('total');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    const fetchReport = async () => {
      if (!startDate || !endDate) {
        setReport(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        setPageTitle('Thống kê nhân viên');

        const startIso = `${startDate}T00:00:00+07:00`;
        const endIso = `${endDate}T23:59:59+07:00`;

        const response = await Report.fetch('order_staff_activity', {
          start_date: startIso,
          end_date: endIso,
        });
        setReport(response);
      } catch (error) {
        console.error('Error fetching report:', error);
        setReport(null);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [startDate, endDate, setPageTitle]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedData = useMemo(() => {
    if (!report?.staff_activity) return [];

    const dataWithTotals = report.staff_activity.map((row) => ({
      ...row,
      total: row.orders_created + row.orders_packed + row.invoices_sent,
    }));

    return [...dataWithTotals].sort((a, b) => {
      const aVal = sortField === 'account_name' ? a.account_name.toLowerCase() : a[sortField];
      const bVal = sortField === 'account_name' ? b.account_name.toLowerCase() : b[sortField];
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [report?.staff_activity, sortField, sortDirection]);

  const totals = useMemo(() => {
    if (!sortedData.length) return { orders_created: 0, orders_packed: 0, invoices_sent: 0, total: 0 };
    return sortedData.reduce(
      (acc, row) => ({
        orders_created: acc.orders_created + row.orders_created,
        orders_packed: acc.orders_packed + row.orders_packed,
        invoices_sent: acc.invoices_sent + row.invoices_sent,
        total: acc.total + row.total,
      }),
      { orders_created: 0, orders_packed: 0, invoices_sent: 0, total: 0 }
    );
  }, [sortedData]);

  if (loading) {
    return <Skeleton variant="rectangular" width="100%" height={600} />;
  }

  if (!report || !report.staff_activity || report.staff_activity.length === 0) {
    return (
      <Box sx={{ maxWidth: 1200, margin: '24px auto' }}>
        <DateRangeFilter
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
        />
        <Paper sx={{ p: 3, textAlign: 'center', mt: 2 }}>
          <Typography variant="h6" color="text.secondary">
            {!startDate || !endDate
              ? 'Vui lòng chọn khoảng thời gian để xem báo cáo'
              : 'Không tìm thấy dữ liệu báo cáo cho khoảng thời gian đã chọn.'}
          </Typography>
        </Paper>
      </Box>
    );
  }

  const columns = [
    { field: 'account_name', label: 'Nhân viên', align: 'left' },
    { field: 'orders_created', label: 'Tạo đơn', align: 'right' },
    { field: 'orders_packed', label: 'Đóng gói', align: 'right' },
    { field: 'invoices_sent', label: 'Gửi hoá đơn', align: 'right' },
    { field: 'total', label: 'Tổng', align: 'right' },
  ];

  return (
    <Box sx={{ maxWidth: 1200, margin: '24px auto', pl: 3, pr: 3 }}>
      <DateRangeFilter
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        sx={{ mb: 3 }}
      />
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 2,
          '& .MuiTableCell-head': {
            backgroundColor: 'primary.main',
            color: 'white',
            fontWeight: 'bold',
          },
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="order staff activity report table">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.field} align={col.align}>
                  <TableSortLabel
                    active={sortField === col.field}
                    direction={sortField === col.field ? sortDirection : 'asc'}
                    onClick={() => handleSort(col.field)}
                    sx={{
                      '&.MuiTableSortLabel-root': { color: 'white' },
                      '&.MuiTableSortLabel-root:hover': { color: 'white' },
                      '&.Mui-active': { color: 'white' },
                      '& .MuiTableSortLabel-icon': { color: 'white !important' },
                    }}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((row) => (
              <TableRow key={row.account_id}>
                <TableCell>{row.account_name}</TableCell>
                <TableCell align="right">{row.orders_created}</TableCell>
                <TableCell align="right">{row.orders_packed}</TableCell>
                <TableCell align="right">{row.invoices_sent}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{row.total}</TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>TỔNG</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.orders_created}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.orders_packed}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.invoices_sent}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.total}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default OrderStaffActivityReport;
